import React, { useState } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Shield,
  Lock,
  Users,
  Cookie,
  Clock,
  Mail
} from 'lucide-react';

const PrivacyPolicy = () => {
  const [activeSection, setActiveSection] = useState(null);

  const sections = [
    {
      id: 'data-collection',
      icon: <Shield className="w-5 h-5" />,
      title: 'What We Collect',
      content: `We collect essential information to process your orders and improve your shopping experience:
        • Account details (name, email, password)
        • Shipping information
        • Payment details (processed securely)
        • Shopping preferences and history
        • Device and browser information`
    },
    {
      id: 'data-usage',
      icon: <Lock className="w-5 h-5" />,
      title: 'How We Use Your Data',
      content: `Your data helps us:
        • Process and fulfill your orders
        • Personalize your shopping experience
        • Improve our product recommendations
        • Send order updates and shipping notifications
        • Prevent fraud and enhance security`
    },
    {
      id: 'data-sharing',
      icon: <Users className="w-5 h-5" />,
      title: 'Information Sharing',
      content: `We share information only with:
        • Payment processors to handle transactions
        • Shipping partners to deliver orders
        • Analytics providers to improve our service
        • We never sell your personal data`
    },
    {
      id: 'cookies',
      icon: <Cookie className="w-5 h-5" />,
      title: 'Cookies & Tracking',
      content: `We use cookies to:
        • Remember your shopping cart
        • Keep you logged in
        • Analyze site performance
        • Personalize your experience
        • Track order status`
    },
    {
      id: 'retention',
      icon: <Clock className="w-5 h-5" />,
      title: 'Data Retention',
      content: `We retain your data for:
        • Active accounts: As long as necessary
        • Order history: 7 years (legal requirement)
        • Shopping preferences: Until account deletion
        • You can request data deletion anytime`
    },
    {
      id: 'contact',
      icon: <Mail className="w-5 h-5" />,
      title: 'Contact Us',
      content: `Questions about your privacy?
        • Email: privacy@kayanmarket.com
        • Address: Kayan Market, Egypt
        • Response time: Within 48 hours`
    }
  ];

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="text-3xl font-bold mb-4">Privacy Policy</h1>
        <p className="text-gray-600">Last Updated: January 17, 2025</p>
      </div>

      {/* Alert Banner */}
      <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6 rounded">
        <p className="text-sm text-blue-700">
          At Kayan Market, we prioritize your privacy and security. This policy explains how we handle your information when you use our online store.
        </p>
      </div>

      {/* Main Content */}
      <div className="space-y-4">
        {sections.map((section) => (
          <div
            key={section.id}
            className="border border-gray-200 rounded-lg overflow-hidden bg-white shadow-sm"
          >
            {/* Section Header */}
            <button
              onClick={() => setActiveSection(activeSection === section.id ? null : section.id)}
              className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between cursor-pointer"
            >
              <div className="flex items-center space-x-3">
                <span className="text-gray-600">{section.icon}</span>
                <span className="font-medium text-lg">{section.title}</span>
              </div>
              {activeSection === section.id ?
                <ChevronUp className="w-5 h-5 text-gray-500" /> :
                <ChevronDown className="w-5 h-5 text-gray-500" />
              }
            </button>

            {/* Section Content */}
            {activeSection === section.id && (
              <div className="p-4 bg-white">
                <p className="text-gray-700 whitespace-pre-line">
                  {section.content}
                </p>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="mt-8 text-center text-sm text-gray-500 space-y-2">
        <p>This privacy policy is compliant with GDPR, CCPA, and other applicable privacy laws.</p>
        <p>© 2025 Kayan Market. All rights reserved.</p>
      </div>
    </div>
  );
};

export default PrivacyPolicy;